import * as React from 'react';
import MediaQuery from 'react-responsive';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTitle,
  AnalyticsProvider,
  breakPoints,
  Card,
  CardContent,
  Grid,
  GridColumn,
  IButtonProps,
  Icon,
  Notice,
  Separator,
  Spinner,
  Text
} from 'telia-front-react';

import ButtonWithAnalytics from '../components/common/ButtonWithAnalytics';

import TemplateBillingSettingsDesktop, { ITemplateBillingSettingsDesktopLabels } from './settings-card-desktop';

export interface ITemplateBillingSettingsCardProps {
  /**
   * Accordion ID.
   */
  id: string;
  /**
   * Accordion title.
   */
  title: string;
  /**
   * Accordion title description.
   */
  titleDescription: string;
  /**
   * Bills table labels.
   */
  labels: ITemplateBillingSettingsDesktopLabels;
  /**
   * Single bill button props.
   */
  singleBillButton: IButtonProps;
  /**
   * Notice content that is shown to advise about single bill button.
   */
  singleBillAdvisoryNoticeContent?: React.ReactNode;
  /**
   * Notice content that is shown when no reference numbers exist.
   */
  noActiveReferenceNumbersNoticeContent: React.ReactNode;
  /**
   * Bills data loading state.
   */
  dataState: 'initial' | 'loading' | 'error' | 'success';
  /**
   * Bills data.
   */
  bills?: ITemplateBillingSettingsCardBill[];
  /**
   * Service settings changed notice.
   */
  dataErrorNoticeContent?: React.ReactNode;
  /**
   * Accordion toggle callback.
   */
  onToggle?: (id: string | null) => void;

  isOpen?: boolean;
}

export interface ITemplateBillingSettingsCardBill {
  icon: string;

  serviceTitle: string;

  referenceNumber: string;

  settingsButton: IButtonProps;

  deliveryChannels: ITemplateBillingSettingsCardDeliveryChannel[];
}

export interface ITemplateBillingSettingsCardDeliveryChannel {
  title: string;

  canTruncateTitle?: boolean;

  description?: string;
}

export interface ITemplateBillingSettingsCardState {
  /**
   * If component is mounted in DOM.
   */
  isMounted: boolean;
}

export interface IGroupProps {
  noticeLabel: string;
  buttonLabel: string;
  isGroupsAllowed: boolean;
  onClick: () => void;
  groupsButtonEnabled: boolean;
}

export default class TemplateBillingSettingsCard extends React.Component<ITemplateBillingSettingsCardProps, ITemplateBillingSettingsCardState> {
  constructor(props: ITemplateBillingSettingsCardProps) {
    super(props);

    this.state = {
      isMounted: false
    };
  }

  public componentDidMount(): void {
    this.setState({
      isMounted: true
    });
  }

  public renderCardDeliveryChannels(deliveryChannels: ITemplateBillingSettingsCardDeliveryChannel[], parentIndex: number): JSX.Element[] {
    return deliveryChannels.map((deliveryChannel: ITemplateBillingSettingsCardDeliveryChannel, index: number) => {
      return (
        <React.Fragment key={index}>
          <div>{deliveryChannel.title}</div>
          {deliveryChannel.description && (
            <div className="text-light text-small" id={`invoice-settings-card-row-delivery-channel-description-${parentIndex}-${index}`}>
              {deliveryChannel.description}
            </div>
          )}
        </React.Fragment>
      );
    });
  }

  public renderBillCards(bills: ITemplateBillingSettingsCardBill[]): JSX.Element[] {
    const { labels } = this.props;

    return bills.map((bill: ITemplateBillingSettingsCardBill, index: number) => (
      <GridColumn key={index} width={['xs-12']} id={`invoice-settings-card-row-${index}`}>
        <Card>
          <CardContent>
            <Text className="text-center">
              {this.renderIcon(bill)}
              <div>
                <div className="text-bold" id={`invoice-settings-card-row-service-title-${index}`}>
                  {bill.serviceTitle}
                </div>
                <div className="text-light" id={`invoice-settings-card-row-reference-number-${index}`}>
                  {bill.referenceNumber}
                </div>
              </div>
              <Grid align={['center-xs']}>
                <GridColumn width={['xs-12']}>
                  <div className="text-left">
                    <div className="text-small">{labels.delivery}</div>
                    {this.renderCardDeliveryChannels(bill.deliveryChannels, index)}
                  </div>
                </GridColumn>
                <GridColumn width={['xs']}>{this.renderButton(bill, `invoice-settings-card-settings-button-${index}`)}</GridColumn>
              </Grid>
            </Text>
          </CardContent>
        </Card>
      </GridColumn>
    ));
  }

  public renderIcon(bill: ITemplateBillingSettingsCardBill): JSX.Element {
    return <Icon size="sm" modifiers={bill.icon} />;
  }

  public renderButton(bill: ITemplateBillingSettingsCardBill, buttonId: string): JSX.Element {
    return (
      <Grid align={['end-xs']} gutter="none">
        <GridColumn width={['xs']}>
          <ButtonWithAnalytics
            gaEvent="CTA - Form - invoice settings"
            buttonProps={{
              id: buttonId,
              iconRight: '#arrow-right',
              size: 'small',
              type: 'primary',
              ...bill.settingsButton
            }}
          />
        </GridColumn>
      </Grid>
    );
  }

  public renderMobileLayout(): JSX.Element {
    const { bills } = this.props;

    return (
      <>
        <Grid>{bills && this.renderBillCards(bills)}</Grid>
        {this.props.singleBillButton && this.renderSingleBillButtonMobile()}
      </>
    );
  }

  public renderDesktopLayout(): JSX.Element {
    return (
      <>
        <TemplateBillingSettingsDesktop bills={this.props.bills} labels={this.props.labels} />
        {this.props.singleBillButton && this.renderSingleBillButtonDesktop()}
      </>
    );
  }

  public renderDynamicLayout(): JSX.Element {
    return (
      <React.Fragment>
        <MediaQuery minWidth={breakPoints.md}>{this.renderDesktopLayout()}</MediaQuery>
        <MediaQuery maxWidth={breakPoints.md - 1}>{this.renderMobileLayout()}</MediaQuery>
      </React.Fragment>
    );
  }

  public renderSingleBillButtonDesktop(): JSX.Element {
    return (
      <React.Fragment>
        <Separator className="hidden-xs hidden-sm" color="medium" spacing="xxsmall" />
        <Grid>
          <GridColumn width={['xs', 'md-6']} align={['middle-xs']}>
            <Grid align={['center-xs', 'start-md']}>
              <GridColumn width={['xs']}>
                {this.props.singleBillAdvisoryNoticeContent && <Notice size="small">{this.props.singleBillAdvisoryNoticeContent}</Notice>}
              </GridColumn>
            </Grid>
          </GridColumn>
          <GridColumn width={['xs', 'md-6']}>
            <Grid align={['center-xs', 'end-md']}>
              <GridColumn width={['xs']}>
                <ButtonWithAnalytics
                  gaEvent="CTA - Form - unify invoices"
                  buttonProps={{
                    iconRight: '#arrow-right',
                    ...this.props.singleBillButton
                  }}
                />
              </GridColumn>
            </Grid>
          </GridColumn>
        </Grid>
      </React.Fragment>
    );
  }

  public renderSingleBillButtonMobile(): JSX.Element {
    return (
      <React.Fragment>
        <Separator className="hidden-xs hidden-sm" color="medium" spacing="xxsmall" />
        <Grid align={['between-xs', 'middle-xs', 'end-md']} gutter="small">
          <GridColumn width={['xs']}>
            {this.props.singleBillAdvisoryNoticeContent && <Notice size="small">{this.props.singleBillAdvisoryNoticeContent}</Notice>}
          </GridColumn>
          <GridColumn width={['xs']}>
            <ButtonWithAnalytics
              gaEvent="CTA - Form - unify invoices"
              buttonProps={{
                iconRight: '#arrow-right',
                ...this.props.singleBillButton
              }}
            />
          </GridColumn>
        </Grid>
      </React.Fragment>
    );
  }

  public renderAccordionContent(): JSX.Element {
    const { bills } = this.props;

    if (this.props.dataState === 'error') {
      return (
        <Notice type="error" align="center">
          {this.props.dataErrorNoticeContent}
        </Notice>
      );
    }

    if (this.props.dataState === 'loading') {
      return <Spinner />;
    }

    return (
      <Text>
        {bills && bills.length === 0 && this.renderNoActiveReferenceNumbersNotice()}
        {bills && bills.length !== 0 && (this.state.isMounted ? this.renderDynamicLayout() : this.renderDesktopLayout())}
      </Text>
    );
  }

  public renderNoActiveReferenceNumbersNotice(): JSX.Element {
    return <Notice size="small">{this.props.noActiveReferenceNumbersNoticeContent}</Notice>;
  }

  public render(): JSX.Element {
    const { title, titleDescription, onToggle, id } = this.props;

    return (
      <AnalyticsProvider group="Invoice settings">
        <Accordion size="large" bordered={false} onToggle={onToggle} openItem={this.props.isOpen && this.props.id}>
          <AccordionItem id={id} icon="#settings">
            <AccordionHeader>
              <Grid verticalGutter="xsmall">
                <GridColumn>
                  <AccordionTitle className="h3 text-bold">{title}</AccordionTitle>
                </GridColumn>
                <GridColumn>{titleDescription}</GridColumn>
              </Grid>
            </AccordionHeader>
            <AccordionContent>{this.renderAccordionContent()}</AccordionContent>
          </AccordionItem>
        </Accordion>
      </AnalyticsProvider>
    );
  }
}
